/**
 * refsCrudApi — RTK Query CRUD slices cho 9 ref tables (Sprint 5).
 *
 * URL pattern: /purchase/refs/{name}/{list,create,{id},update,delete}
 *
 * Một slice riêng cho mỗi ref để giữ type signature riêng biệt.
 * Tag enhanced shared: 'Refs' (mỗi ref có id namespace prefix).
 *
 * Refs (9):
 *   - certifications
 *   - delivery-places
 *   - delivery-terms
 *   - packages
 *   - adjust-lots
 *   - licenses (admin)
 *   - qualities  (list_and_valo)
 *   - updates
 *   - calendars  (market_calendar)
 */
import { baseApi } from './baseApi'
import type {
  AdjustLot,
  AdminLicense,
  Certification,
  DataResponse,
  DeliveryPlace,
  DeliveryTerm,
  MarketCalendar,
  MarketHoliday,
  MarketMonth,
  MasterDataCounterPartyBank,
  MasterDataCounterPartyBankCreate,
  MasterDataCounterPartyBankUpdate,
  MasterDataLicense,
  MasterDataLicenseCreate,
  MasterDataLicenseUpdate,
  MasterDataService,
  MasterDataServiceCreate,
  MasterDataServiceUpdate,
  MasterDataDocDispatch,
  MasterDataDocDispatchCreate,
  MasterDataDocDispatchUpdate,
  MasterDataGeneralInfo,
  MasterDataGeneralInfoCreate,
  MasterDataGeneralInfoUpdate,
  MasterDataValuation,
  MasterDataValuationCreate,
  MasterDataValuationUpdate,
  MasterDataUpdate,
  MasterDataUpdateCreate,
  MasterDataUpdateUpdate,
  MasterDataTypeOfInspection,
  MasterDataTypeOfInspectionCreate,
  MasterDataTypeOfInspectionUpdate,
  MasterDataDunnage,
  MasterDataDunnageCreate,
  MasterDataDunnageUpdate,
  MasterDataContractType,
  MasterDataContractTypeCreate,
  MasterDataContractTypeUpdate,
  MasterDataBank,
  MasterDataBankCreate,
  MasterDataBankUpdate,
  MasterDataCertification,
  MasterDataCertificationCreate,
  MasterDataCertificationUpdate,
  MasterDataCertificationPremiumChange,
  MasterDataCertificationPremiumChangeCreate,
  MasterDataCounterParty,
  MasterDataCounterPartyCreate,
  MasterDataCounterPartyUpdate,
  MasterDataDeliveryTerm,
  MasterDataDeliveryTermCreate,
  MasterDataDeliveryTermUpdate,
  MasterDataPackage,
  MasterDataPackageCreate,
  MasterDataPackageUpdate,
  MasterDataPackageChange,
  MasterDataPackageChangeCreate,
  MasterDataDeliveryPlace,
  MasterDataDeliveryPlaceCreate,
  MasterDataDeliveryPlaceUpdate,
  MasterDataTlcBank,
  MasterDataTlcBankCreate,
  MasterDataTlcBankUpdate,
  MasterDataConsignee,
  MasterDataConsigneeCreate,
  MasterDataConsigneeUpdate,
  MasterDataCvbbType,
  MasterDataCvbbTypeCreate,
  MasterDataCvbbTypeUpdate,
  MasterDataExportTerminal,
  MasterDataExportTerminalCreate,
  MasterDataExportTerminalUpdate,
  MasterDataVesselName,
  MasterDataVesselNameCreate,
  MasterDataVesselNameUpdate,
  MasterDataLogisticCost,
  MasterDataLogisticCostCreate,
  MasterDataLogisticCostUpdate,
  MasterDataShippingDoc,
  MasterDataShippingDocCreate,
  MasterDataShippingDocUpdate,
  MasterDataTypeOfClaim,
  MasterDataTypeOfClaimCreate,
  MasterDataTypeOfClaimUpdate,
  MasterDataClaimCondition,
  MasterDataClaimConditionCreate,
  MasterDataClaimConditionUpdate,
  MasterDataListWarehouse,
  MasterDataListWarehouseCreate,
  MasterDataListWarehouseUpdate,
  MasterDataWarehouseSuggest,
  MasterDataProcessingType,
  MasterDataProcessingTypeCreate,
  MasterDataProcessingTypeUpdate,
  MasterDataProcessingTypeSuggest,
  MasterDataServiceType,
  MasterDataServiceTypeCreateIn,
  MasterDataServiceTypeUpdateIn,
  ServiceTypeOption,
  MessageResponse,
  PageResponse,
  Package,
  Quality,
  RefListQuery,
  Update,
} from '@/types/api'

const refsEnhanced = baseApi.enhanceEndpoints({ addTagTypes: ['Refs'] })

/** Helper builder: tạo 4 endpoints CRUD chuẩn cho 1 ref name. */
function buildRefEndpoints<T, Id extends string | number>(
  name: string,
  /** Lấy id (PK) từ row — dùng cho PUT/DELETE URL. */
  _getId: (row: T) => Id,
) {
  // KHÔNG dùng trực tiếp trong slice; mỗi slice gọi riêng để giữ tag id
  // nhất quán. Đây chỉ là interface gợi ý — implement inline trong từng slice.
  return { name, _getId }
}
void buildRefEndpoints // tránh tree-shake warning

// ---------- 1. certifications ----------
const certificationsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listCertifications: b.query<PageResponse<Certification>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/certifications/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
    createCertification: b.mutation<DataResponse<Certification>, Certification>({
      query: (body) => ({
        url: '/purchase/refs/certifications/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
    updateCertification: b.mutation<
      DataResponse<Certification>,
      { id: string; body: Partial<Certification> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/certifications/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
    deleteCertification: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/certifications/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'certifications-LIST' }],
    }),
  }),
})

// ---------- 2. delivery-places ----------
const deliveryPlacesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listDeliveryPlacesCrud: b.query<PageResponse<DeliveryPlace>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/delivery-places/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
    createDeliveryPlace: b.mutation<DataResponse<DeliveryPlace>, DeliveryPlace>({
      query: (body) => ({
        url: '/purchase/refs/delivery-places/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
    updateDeliveryPlace: b.mutation<
      DataResponse<DeliveryPlace>,
      { id: string; body: Partial<DeliveryPlace> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/delivery-places/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
    deleteDeliveryPlace: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/delivery-places/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-places-LIST' }],
    }),
  }),
})

// ---------- 3. delivery-terms ----------
const deliveryTermsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listDeliveryTermsCrud: b.query<PageResponse<DeliveryTerm>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/delivery-terms/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
    createDeliveryTerm: b.mutation<DataResponse<DeliveryTerm>, DeliveryTerm>({
      query: (body) => ({
        url: '/purchase/refs/delivery-terms/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
    updateDeliveryTerm: b.mutation<
      DataResponse<DeliveryTerm>,
      { id: string; body: Partial<DeliveryTerm> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/delivery-terms/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
    deleteDeliveryTerm: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/delivery-terms/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'delivery-terms-LIST' }],
    }),
  }),
})

// ---------- 4. packages ----------
const packagesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listPackagesCrud: b.query<PageResponse<Package>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/packages/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
    createPackage: b.mutation<DataResponse<Package>, Package>({
      query: (body) => ({
        url: '/purchase/refs/packages/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
    updatePackage: b.mutation<
      DataResponse<Package>,
      { id: string; body: Partial<Package> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/packages/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
    deletePackage: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/packages/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'packages-LIST' }],
    }),
  }),
})

// ---------- 5. adjust-lots ----------
const adjustLotsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listAdjustLots: b.query<PageResponse<AdjustLot>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/adjust-lots/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
    createAdjustLot: b.mutation<DataResponse<AdjustLot>, AdjustLot>({
      query: (body) => ({
        url: '/purchase/refs/adjust-lots/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
    updateAdjustLot: b.mutation<
      DataResponse<AdjustLot>,
      { id: string | number; body: Partial<AdjustLot> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/adjust-lots/${encodeURIComponent(String(id))}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
    deleteAdjustLot: b.mutation<MessageResponse, string | number>({
      query: (id) => ({
        url: `/purchase/refs/adjust-lots/${encodeURIComponent(String(id))}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'adjust-lots-LIST' }],
    }),
  }),
})

// ---------- 6. licenses (admin) ----------
const licensesAdminRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listAdminLicenses: b.query<PageResponse<AdminLicense>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/licenses/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
    createAdminLicense: b.mutation<DataResponse<AdminLicense>, Partial<AdminLicense>>({
      query: (body) => ({
        url: '/purchase/refs/licenses/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
    updateAdminLicense: b.mutation<
      DataResponse<AdminLicense>,
      { id: number; body: Partial<AdminLicense> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/licenses/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
    deleteAdminLicense: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/refs/licenses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'licenses-LIST' }],
    }),
  }),
})

// ---------- 7. qualities (list_and_valo) ----------
const qualitiesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listQualitiesCrud: b.query<PageResponse<Quality>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/qualities/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
    createQuality: b.mutation<DataResponse<Quality>, Quality>({
      query: (body) => ({
        url: '/purchase/refs/qualities/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
    updateQuality: b.mutation<
      DataResponse<Quality>,
      { id: string; body: Partial<Quality> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/qualities/${encodeURIComponent(id)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
    deleteQuality: b.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/purchase/refs/qualities/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'qualities-LIST' }],
    }),
  }),
})

// ---------- 8. updates ----------
const updatesRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listUpdates: b.query<PageResponse<Update>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/updates/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
    createUpdate: b.mutation<DataResponse<Update>, Update>({
      query: (body) => ({
        url: '/purchase/refs/updates/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
    updateUpdate: b.mutation<
      DataResponse<Update>,
      { id: number; body: Partial<Update> }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/refs/updates/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
    deleteUpdate: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/refs/updates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'updates-LIST' }],
    }),
  }),
})

// ---------- 9. calendars (market_calendar — composite PK code/ex_text) ----------
const calendarsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listCalendars: b.query<PageResponse<MarketCalendar>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/calendars/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
    createCalendar: b.mutation<DataResponse<MarketCalendar>, MarketCalendar>({
      query: (body) => ({
        url: '/purchase/refs/calendars/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
    updateCalendar: b.mutation<
      DataResponse<MarketCalendar>,
      { code: string; ex_text: string; body: Partial<MarketCalendar> }
    >({
      query: ({ code, ex_text, body }) => ({
        url: `/purchase/refs/calendars/${encodeURIComponent(code)}/${encodeURIComponent(ex_text)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
    deleteCalendar: b.mutation<MessageResponse, { code: string; ex_text: string }>({
      query: ({ code, ex_text }) => ({
        url: `/purchase/refs/calendars/${encodeURIComponent(code)}/${encodeURIComponent(ex_text)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'calendars-LIST' }],
    }),
  }),
})

// ---------- 10. months (market_month — composite PK market/code) ----------
const monthsRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMonths: b.query<PageResponse<MarketMonth>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/month/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'months-LIST' }],
    }),
    createMonth: b.mutation<DataResponse<MarketMonth>, MarketMonth>({
      query: (body) => ({
        url: '/master-data/month/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'months-LIST' }],
    }),
    updateMonth: b.mutation<
      DataResponse<MarketMonth>,
      { market: string; code: string; body: Partial<MarketMonth> }
    >({
      query: ({ market, code, body }) => ({
        url: `/master-data/month/${encodeURIComponent(market)}/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'months-LIST' }],
    }),
    deleteMonth: b.mutation<MessageResponse, { market: string; code: string }>({
      query: ({ market, code }) => ({
        url: `/master-data/month/${encodeURIComponent(market)}/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'months-LIST' }],
    }),
  }),
})

// ---------- 11. holidays (market_holiday — composite PK date/market) ----------
const holidaysRefApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listHolidays: b.query<PageResponse<MarketHoliday>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/holiday/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'holidays-LIST' }],
    }),
    createHoliday: b.mutation<DataResponse<MarketHoliday>, MarketHoliday>({
      query: (body) => ({
        url: '/master-data/holiday/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'holidays-LIST' }],
    }),
    updateHoliday: b.mutation<
      DataResponse<MarketHoliday>,
      { date: string; market: string; body: Partial<MarketHoliday> }
    >({
      query: ({ date, market, body }) => ({
        url: `/master-data/holiday/${encodeURIComponent(date)}/${encodeURIComponent(market)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'holidays-LIST' }],
    }),
    deleteHoliday: b.mutation<MessageResponse, { date: string; market: string }>({
      query: ({ date, market }) => ({
        url: `/master-data/holiday/${encodeURIComponent(date)}/${encodeURIComponent(market)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'holidays-LIST' }],
    }),
  }),
})

// ---------- 12. master-data counter-party-bank (cp_bank — composite PK bank_account/cp_code/bank) ----------
// Access frmMdQryCPBank. API RIÊNG /master-data/counter-party-bank/* (tách khỏi refs
// cũ). Composite PK 3 phần → PUT/DELETE qua path segments /{bank_account}/{cp_code}/{bank}.
const masterDataCounterPartyBankApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdCounterPartyBanks: b.query<PageResponse<MasterDataCounterPartyBank>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/counter-party-bank/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-counter-party-bank-LIST' }],
    }),
    createMdCounterPartyBank: b.mutation<DataResponse<MasterDataCounterPartyBank>, MasterDataCounterPartyBankCreate>({
      query: (body) => ({ url: '/master-data/counter-party-bank/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-counter-party-bank-LIST' }],
    }),
    updateMdCounterPartyBank: b.mutation<
      DataResponse<MasterDataCounterPartyBank>,
      { bank_account: string; cp_code: string; bank: string; body: MasterDataCounterPartyBankUpdate }
    >({
      query: ({ bank_account, cp_code, bank, body }) => ({
        url: `/master-data/counter-party-bank/${encodeURIComponent(bank_account)}/${encodeURIComponent(cp_code)}/${encodeURIComponent(bank)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-counter-party-bank-LIST' }],
    }),
    deleteMdCounterPartyBank: b.mutation<
      MessageResponse,
      { bank_account: string; cp_code: string; bank: string }
    >({
      query: ({ bank_account, cp_code, bank }) => ({
        url: `/master-data/counter-party-bank/${encodeURIComponent(bank_account)}/${encodeURIComponent(cp_code)}/${encodeURIComponent(bank)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-counter-party-bank-LIST' }],
    }),
  }),
})

// ---------- 13. master-data license (Access frmMdQryLicense) ----------
// Stack RIÊNG /master-data/license/* (đọc/ghi CÙNG bảng `license` với refs stack;
// refs giữ nguyên cho LicensesAdminPage + /vendors/*/licenses). Single PK license_id.
const masterDataLicensesApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdLicenses: b.query<PageResponse<MasterDataLicense>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/license/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-license-LIST' }],
    }),
    createMdLicense: b.mutation<DataResponse<MasterDataLicense>, MasterDataLicenseCreate>({
      query: (body) => ({ url: '/master-data/license/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-license-LIST' }],
    }),
    updateMdLicense: b.mutation<
      DataResponse<MasterDataLicense>,
      { id: number; body: MasterDataLicenseUpdate }
    >({
      query: ({ id, body }) => ({ url: `/master-data/license/${id}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-license-LIST' }],
    }),
    deleteMdLicense: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/master-data/license/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-license-LIST' }],
    }),
  }),
})

// ---------- 14. master-data services (Access frmMdQryService) ----------
// PK service_code auto-suggest theo service_type. Kèm 2 endpoint phụ:
//   service-types (RowSource combo) + suggest-code (VBA suggest parity).
const masterDataServicesApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdServices: b.query<PageResponse<MasterDataService>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/service/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-service-LIST' }],
    }),
    listServiceTypes: b.query<ServiceTypeOption[], void>({
      query: () => ({ url: '/master-data/service/service-types' }),
      transformResponse: (r: DataResponse<ServiceTypeOption[]>) => r.data ?? [],
      providesTags: [{ type: 'Refs', id: 'service-types' }],
    }),
    suggestServiceCode: b.query<{ service_code: string }, string>({
      query: (serviceType) => ({
        url: '/master-data/service/suggest-code',
        params: { service_type: serviceType },
      }),
      transformResponse: (r: DataResponse<{ service_code: string }>) =>
        r.data ?? { service_code: '' },
      keepUnusedDataFor: 0,
    }),
    createMdService: b.mutation<DataResponse<MasterDataService>, MasterDataServiceCreate>({
      query: (body) => ({
        url: '/master-data/service/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-service-LIST' }],
    }),
    updateMdService: b.mutation<
      DataResponse<MasterDataService>,
      { code: string; body: MasterDataServiceUpdate }
    >({
      query: ({ code, body }) => ({
        url: `/master-data/service/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-service-LIST' }],
    }),
    deleteMdService: b.mutation<MessageResponse, string>({
      query: (code) => ({
        url: `/master-data/service/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-service-LIST' }],
    }),
  }),
})

// ---------- 15. master-data service-types (Access frmMdQryServiceType) ----------
// CRUD `service_type` (Master Data > CP List > Service Type). PK
// service_type_code (text, lowercase). Tách khỏi Service combo lookup
// (`listServiceTypes` ở masterDataServicesApi trỏ /services/service-types).
const masterDataServiceTypesApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdServiceTypes: b.query<PageResponse<MasterDataServiceType>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/service-type/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-service-type-LIST' }],
    }),
    createMdServiceType: b.mutation<DataResponse<MasterDataServiceType>, MasterDataServiceTypeCreateIn>({
      query: (body) => ({
        url: '/master-data/service-type/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-service-type-LIST' }],
    }),
    updateMdServiceType: b.mutation<
      DataResponse<MasterDataServiceType>,
      { code: string; body: MasterDataServiceTypeUpdateIn }
    >({
      query: ({ code, body }) => ({
        url: `/master-data/service-type/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-service-type-LIST' }],
    }),
    deleteMdServiceType: b.mutation<MessageResponse, string>({
      query: (code) => ({
        url: `/master-data/service-type/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-service-type-LIST' }],
    }),
  }),
})

// ---------- 15. master-data docs-dispatch (Access frmMdQryDocsDispatch) ----------
const masterDataDocsDispatchApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdDocsDispatch: b.query<PageResponse<MasterDataDocDispatch>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/doc-dispatch/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-doc-dispatch-LIST' }],
    }),
    createMdDocDispatch: b.mutation<DataResponse<MasterDataDocDispatch>, MasterDataDocDispatchCreate>({
      query: (body) => ({
        url: '/master-data/doc-dispatch/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-doc-dispatch-LIST' }],
    }),
    updateMdDocDispatch: b.mutation<
      DataResponse<MasterDataDocDispatch>,
      { code: string; body: MasterDataDocDispatchUpdate }
    >({
      query: ({ code, body }) => ({
        url: `/master-data/doc-dispatch/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-doc-dispatch-LIST' }],
    }),
    deleteMdDocDispatch: b.mutation<MessageResponse, string>({
      query: (code) => ({
        url: `/master-data/doc-dispatch/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-doc-dispatch-LIST' }],
    }),
    // access-suggest: gợi ý mã code kế tiếp (SD-<n>) + blur-check trùng.
    suggestMdDocDispatchCode: b.query<{ code: string }, void>({
      query: () => ({ url: '/master-data/doc-dispatch/suggest-code' }),
      transformResponse: (r: DataResponse<{ code: string }>) => r.data ?? { code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdDocDispatchCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({
        url: '/master-data/doc-dispatch/check-code',
        params: { code },
      }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 26. master-data contract-type (Access frmMdQryContractType) ----------
const masterDataContractTypeApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdContractTypes: b.query<PageResponse<MasterDataContractType>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/contract-type/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-contract-type-LIST' }],
    }),
    createMdContractType: b.mutation<DataResponse<MasterDataContractType>, MasterDataContractTypeCreate>({
      query: (body) => ({ url: '/master-data/contract-type/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-contract-type-LIST' }],
    }),
    updateMdContractType: b.mutation<DataResponse<MasterDataContractType>, { code: string; body: MasterDataContractTypeUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/contract-type/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-contract-type-LIST' }],
    }),
    deleteMdContractType: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/contract-type/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-contract-type-LIST' }],
    }),
  }),
})

// ---------- 27. master-data bank (Access frmMdQryBank) ----------
const masterDataBankApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdBanks: b.query<PageResponse<MasterDataBank>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/bank/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-bank-LIST' }],
    }),
    createMdBank: b.mutation<DataResponse<MasterDataBank>, MasterDataBankCreate>({
      query: (body) => ({ url: '/master-data/bank/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-bank-LIST' }],
    }),
    updateMdBank: b.mutation<DataResponse<MasterDataBank>, { code: string; body: MasterDataBankUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/bank/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-bank-LIST' }],
    }),
    deleteMdBank: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/bank/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-bank-LIST' }],
    }),
  }),
})

// ---------- 28. master-data certification (Access frmMdQryCertification; reuse /certifications) ----------
const masterDataCertificationApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdCertifications: b.query<PageResponse<MasterDataCertification>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/certification/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-certification-LIST' }],
    }),
    createMdCertification: b.mutation<DataResponse<MasterDataCertification>, MasterDataCertificationCreate>({
      query: (body) => ({ url: '/master-data/certification/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-certification-LIST' }],
    }),
    updateMdCertification: b.mutation<DataResponse<MasterDataCertification>, { code: string; body: MasterDataCertificationUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/certification/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-certification-LIST' }],
    }),
    deleteMdCertification: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/certification/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-certification-LIST' }],
    }),
    // Premium change-history (Access frmMdQryCertificationAddver2 → certification_change).
    listMdCertificationPremiumChanges: b.query<PageResponse<MasterDataCertificationPremiumChange>, string>({
      query: (certification) => ({ url: `/purchase/refs/certifications/${encodeURIComponent(certification)}/premium-changes` }),
      providesTags: [{ type: 'Refs', id: 'md-certification-changes-LIST' }],
    }),
    addMdCertificationPremiumChange: b.mutation<DataResponse<MasterDataCertificationPremiumChange>, { certification: string; body: MasterDataCertificationPremiumChangeCreate }>({
      query: ({ certification, body }) => ({ url: `/purchase/refs/certifications/${encodeURIComponent(certification)}/premium-changes`, method: 'POST', body }),
      invalidatesTags: [
        { type: 'Refs', id: 'md-certification-changes-LIST' },
        { type: 'Refs', id: 'md-certification-LIST' },
      ],
    }),
    deleteMdCertificationPremiumChange: b.mutation<MessageResponse, { certification: string; dateChange: string }>({
      query: ({ certification, dateChange }) => ({ url: `/purchase/refs/certifications/${encodeURIComponent(certification)}/premium-changes/${encodeURIComponent(dateChange)}`, method: 'DELETE' }),
      invalidatesTags: [
        { type: 'Refs', id: 'md-certification-changes-LIST' },
        { type: 'Refs', id: 'md-certification-LIST' },
      ],
    }),
  }),
})

// ---------- 29. master-data delivery-term (Access frmMdQryDeliveryTerm) ----------
// API RIÊNG /master-data/delivery-term/* (tách hẳn khỏi /purchase/refs/*).
const masterDataDeliveryTermApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdDeliveryTerms: b.query<PageResponse<MasterDataDeliveryTerm>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/delivery-term/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-delivery-term-LIST' }],
    }),
    createMdDeliveryTerm: b.mutation<DataResponse<MasterDataDeliveryTerm>, MasterDataDeliveryTermCreate>({
      query: (body) => ({ url: '/master-data/delivery-term/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-delivery-term-LIST' }],
    }),
    updateMdDeliveryTerm: b.mutation<DataResponse<MasterDataDeliveryTerm>, { code: string; body: MasterDataDeliveryTermUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/delivery-term/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-delivery-term-LIST' }],
    }),
    deleteMdDeliveryTerm: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/delivery-term/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-delivery-term-LIST' }],
    }),
  }),
})

// ---------- 29a. master-data counter-party (Access frmMdQryVendor, cp_vn, PK code) ----------
// API RIÊNG /master-data/counter-party/* (tách hẳn khỏi /vendors cũ). Single PK code.
const masterDataCounterPartyApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdCounterParties: b.query<PageResponse<MasterDataCounterParty>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/counter-party/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-counter-party-LIST' }],
    }),
    createMdCounterParty: b.mutation<DataResponse<MasterDataCounterParty>, MasterDataCounterPartyCreate>({
      query: (body) => ({ url: '/master-data/counter-party/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-counter-party-LIST' }],
    }),
    updateMdCounterParty: b.mutation<DataResponse<MasterDataCounterParty>, { code: string; body: MasterDataCounterPartyUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/counter-party/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-counter-party-LIST' }],
    }),
    deleteMdCounterParty: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/counter-party/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-counter-party-LIST' }],
    }),
  }),
})

// ---------- 29b. master-data calendar (Access frmMdQryCalendar) ----------
// API RIÊNG /master-data/calendar/* (tách khỏi /purchase/refs/calendars — slice
// cũ calendarsRefApi vẫn dùng cho refs/CalendarsPage). Composite PK code/ex_text.
const masterDataCalendarApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdCalendars: b.query<PageResponse<MarketCalendar>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/calendar/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-calendar-LIST' }],
    }),
    createMdCalendar: b.mutation<DataResponse<MarketCalendar>, MarketCalendar>({
      query: (body) => ({ url: '/master-data/calendar/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-calendar-LIST' }],
    }),
    updateMdCalendar: b.mutation<DataResponse<MarketCalendar>, { code: string; ex_text: string; body: Partial<MarketCalendar> }>({
      query: ({ code, ex_text, body }) => ({ url: `/master-data/calendar/${encodeURIComponent(code)}/${encodeURIComponent(ex_text)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-calendar-LIST' }],
    }),
    deleteMdCalendar: b.mutation<MessageResponse, { code: string; ex_text: string }>({
      query: ({ code, ex_text }) => ({ url: `/master-data/calendar/${encodeURIComponent(code)}/${encodeURIComponent(ex_text)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-calendar-LIST' }],
    }),
  }),
})

// ---------- 30. master-data package (Access frmMdQryPackage; reuse /packages) ----------
const masterDataPackageApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdPackages: b.query<PageResponse<MasterDataPackage>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/package/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-package-LIST' }],
    }),
    createMdPackage: b.mutation<DataResponse<MasterDataPackage>, MasterDataPackageCreate>({
      query: (body) => ({ url: '/master-data/package/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-package-LIST' }],
    }),
    updateMdPackage: b.mutation<DataResponse<MasterDataPackage>, { code: string; body: MasterDataPackageUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/package/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-package-LIST' }],
    }),
    deleteMdPackage: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/package/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-package-LIST' }],
    }),
    // Enhancement: gợi ý packaging_code kế tiếp (PACK<nn>) — access-suggest, pre-fill New.
    suggestMdPackageCode: b.query<{ packaging_code: string }, void>({
      query: () => ({ url: '/master-data/package/suggest-code' }),
      transformResponse: (r: DataResponse<{ packaging_code: string }>) => r.data ?? { packaging_code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdPackageCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({ url: '/master-data/package/check-code', params: { packaging_code: code } }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
    // Change-history prem_discount + nw_per_bag_kg (Access frmMdQryPackageAddver2 →
    // package_change). Giữ trên legacy refs-admin endpoint (như certification premium-changes).
    listMdPackageChanges: b.query<PageResponse<MasterDataPackageChange>, string>({
      query: (packagingCode) => ({ url: `/purchase/refs/packages/${encodeURIComponent(packagingCode)}/package-changes` }),
      providesTags: [{ type: 'Refs', id: 'md-package-changes-LIST' }],
    }),
    addMdPackageChange: b.mutation<DataResponse<MasterDataPackageChange>, { packagingCode: string; body: MasterDataPackageChangeCreate }>({
      query: ({ packagingCode, body }) => ({ url: `/purchase/refs/packages/${encodeURIComponent(packagingCode)}/package-changes`, method: 'POST', body }),
      invalidatesTags: [
        { type: 'Refs', id: 'md-package-changes-LIST' },
        { type: 'Refs', id: 'md-package-LIST' },
      ],
    }),
    deleteMdPackageChange: b.mutation<MessageResponse, { packagingCode: string; dateChange: string }>({
      query: ({ packagingCode, dateChange }) => ({ url: `/purchase/refs/packages/${encodeURIComponent(packagingCode)}/package-changes/${encodeURIComponent(dateChange)}`, method: 'DELETE' }),
      invalidatesTags: [
        { type: 'Refs', id: 'md-package-changes-LIST' },
        { type: 'Refs', id: 'md-package-LIST' },
      ],
    }),
  }),
})

// ---------- 31. master-data delivery-place (Access frmMdQryDeliveryPlace; reuse /delivery-places) ----------
const masterDataDeliveryPlaceApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdDeliveryPlaces: b.query<PageResponse<MasterDataDeliveryPlace>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/delivery-place/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-delivery-place-LIST' }],
    }),
    createMdDeliveryPlace: b.mutation<DataResponse<MasterDataDeliveryPlace>, MasterDataDeliveryPlaceCreate>({
      query: (body) => ({ url: '/master-data/delivery-place/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-delivery-place-LIST' }],
    }),
    updateMdDeliveryPlace: b.mutation<DataResponse<MasterDataDeliveryPlace>, { code: string; body: MasterDataDeliveryPlaceUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/delivery-place/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-delivery-place-LIST' }],
    }),
    deleteMdDeliveryPlace: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/delivery-place/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-delivery-place-LIST' }],
    }),
    // Enhancement: gợi ý delivery_place_code kế tiếp (PLACE<nn>) — access-suggest, pre-fill New.
    suggestMdDeliveryPlaceCode: b.query<{ delivery_place_code: string }, void>({
      query: () => ({ url: '/master-data/delivery-place/suggest-code' }),
      transformResponse: (r: DataResponse<{ delivery_place_code: string }>) => r.data ?? { delivery_place_code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdDeliveryPlaceCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({ url: '/master-data/delivery-place/check-code', params: { delivery_place_code: code } }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 32. master-data tlc-bank (Access frmMdQryTLCBank) ----------
const masterDataTlcBankApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdTlcBanks: b.query<PageResponse<MasterDataTlcBank>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/tlc-bank/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-tlc-bank-LIST' }],
    }),
    createMdTlcBank: b.mutation<DataResponse<MasterDataTlcBank>, MasterDataTlcBankCreate>({
      query: (body) => ({ url: '/master-data/tlc-bank/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-tlc-bank-LIST' }],
    }),
    updateMdTlcBank: b.mutation<DataResponse<MasterDataTlcBank>, { code: string; body: MasterDataTlcBankUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/tlc-bank/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-tlc-bank-LIST' }],
    }),
    deleteMdTlcBank: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/tlc-bank/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-tlc-bank-LIST' }],
    }),
  }),
})

// ---------- 33. master-data consignee (Access frmMdQryConsignee) ----------
const masterDataConsigneeApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdConsignees: b.query<PageResponse<MasterDataConsignee>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/consignee/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-consignee-LIST' }],
    }),
    createMdConsignee: b.mutation<DataResponse<MasterDataConsignee>, MasterDataConsigneeCreate>({
      query: (body) => ({ url: '/master-data/consignee/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-consignee-LIST' }],
    }),
    updateMdConsignee: b.mutation<DataResponse<MasterDataConsignee>, { code: string; body: MasterDataConsigneeUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/consignee/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-consignee-LIST' }],
    }),
    deleteMdConsignee: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/consignee/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-consignee-LIST' }],
    }),
    // access-suggest: auto-gen consignee_code (CSN<nnn>) + blur-check trùng.
    suggestMdConsigneeCode: b.query<{ consignee_code: string }, void>({
      query: () => ({ url: '/master-data/consignee/suggest-code' }),
      transformResponse: (r: DataResponse<{ consignee_code: string }>) =>
        r.data ?? { consignee_code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdConsigneeCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({
        url: '/master-data/consignee/check-code',
        params: { consignee_code: code },
      }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 34. master-data cvbb-type (Access frmMdQryCVBBType, auto-int cvbb_id) ----------
const masterDataCvbbTypeApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdCvbbTypes: b.query<PageResponse<MasterDataCvbbType>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/cvbb-type/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-cvbb-type-LIST' }],
    }),
    createMdCvbbType: b.mutation<DataResponse<MasterDataCvbbType>, MasterDataCvbbTypeCreate>({
      query: (body) => ({ url: '/master-data/cvbb-type/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-cvbb-type-LIST' }],
    }),
    updateMdCvbbType: b.mutation<DataResponse<MasterDataCvbbType>, { id: number; body: MasterDataCvbbTypeUpdate }>({
      query: ({ id, body }) => ({ url: `/master-data/cvbb-type/${id}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-cvbb-type-LIST' }],
    }),
    deleteMdCvbbType: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/master-data/cvbb-type/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-cvbb-type-LIST' }],
    }),
  }),
})

// ---------- 35. master-data export-terminal (Access frmMdQryExportTerminal, auto-int id) ----------
const masterDataExportTerminalApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdExportTerminals: b.query<PageResponse<MasterDataExportTerminal>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/export-terminal/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-export-terminal-LIST' }],
    }),
    createMdExportTerminal: b.mutation<DataResponse<MasterDataExportTerminal>, MasterDataExportTerminalCreate>({
      query: (body) => ({ url: '/master-data/export-terminal/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-export-terminal-LIST' }],
    }),
    updateMdExportTerminal: b.mutation<DataResponse<MasterDataExportTerminal>, { id: number; body: MasterDataExportTerminalUpdate }>({
      query: ({ id, body }) => ({ url: `/master-data/export-terminal/${id}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-export-terminal-LIST' }],
    }),
    deleteMdExportTerminal: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/master-data/export-terminal/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-export-terminal-LIST' }],
    }),
  }),
})

// ---------- 36. master-data vessel-name (Access frmMdQryVesselName, auto-int id) ----------
const masterDataVesselNameApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdVesselNames: b.query<PageResponse<MasterDataVesselName>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/vessel-name/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-vessel-name-LIST' }],
    }),
    createMdVesselName: b.mutation<DataResponse<MasterDataVesselName>, MasterDataVesselNameCreate>({
      query: (body) => ({ url: '/master-data/vessel-name/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-vessel-name-LIST' }],
    }),
    updateMdVesselName: b.mutation<DataResponse<MasterDataVesselName>, { id: number; body: MasterDataVesselNameUpdate }>({
      query: ({ id, body }) => ({ url: `/master-data/vessel-name/${id}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-vessel-name-LIST' }],
    }),
    deleteMdVesselName: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/master-data/vessel-name/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-vessel-name-LIST' }],
    }),
  }),
})

// ---------- 37. master-data logistic-cost (Access frmMdQryLogisticCost, auto-int id) ----------
const masterDataLogisticCostApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdLogisticCosts: b.query<PageResponse<MasterDataLogisticCost>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/logistic-cost/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-logistic-cost-LIST' }],
    }),
    createMdLogisticCost: b.mutation<DataResponse<MasterDataLogisticCost>, MasterDataLogisticCostCreate>({
      query: (body) => ({ url: '/master-data/logistic-cost/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-logistic-cost-LIST' }],
    }),
    updateMdLogisticCost: b.mutation<DataResponse<MasterDataLogisticCost>, { id: number; body: MasterDataLogisticCostUpdate }>({
      query: ({ id, body }) => ({ url: `/master-data/logistic-cost/${id}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-logistic-cost-LIST' }],
    }),
    deleteMdLogisticCost: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/master-data/logistic-cost/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-logistic-cost-LIST' }],
    }),
  }),
})

// ---------- 21. master-data shipping-doc (Access frmMdQryShippingDoc) ----------
const masterDataShippingDocApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdShippingDocs: b.query<PageResponse<MasterDataShippingDoc>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/shipping-doc/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-shipping-doc-LIST' }],
    }),
    createMdShippingDoc: b.mutation<DataResponse<MasterDataShippingDoc>, MasterDataShippingDocCreate>({
      query: (body) => ({ url: '/master-data/shipping-doc/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-shipping-doc-LIST' }],
    }),
    updateMdShippingDoc: b.mutation<DataResponse<MasterDataShippingDoc>, { code: string; body: MasterDataShippingDocUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/shipping-doc/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-shipping-doc-LIST' }],
    }),
    deleteMdShippingDoc: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/shipping-doc/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-shipping-doc-LIST' }],
    }),
    suggestMdShippingDocCode: b.query<{ shipping_doc_code: string }, void>({
      query: () => ({ url: '/master-data/shipping-doc/suggest-code' }),
      transformResponse: (r: DataResponse<{ shipping_doc_code: string }>) => r.data ?? { shipping_doc_code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdShippingDocCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({ url: '/master-data/shipping-doc/check-code', params: { shipping_doc_code: code } }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 22. master-data type-of-claim (Access frmMdQryTypeOfClaim) ----------
const masterDataTypeOfClaimApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdTypeOfClaims: b.query<PageResponse<MasterDataTypeOfClaim>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/type-of-claim/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-type-of-claim-LIST' }],
    }),
    createMdTypeOfClaim: b.mutation<DataResponse<MasterDataTypeOfClaim>, MasterDataTypeOfClaimCreate>({
      query: (body) => ({ url: '/master-data/type-of-claim/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-type-of-claim-LIST' }],
    }),
    updateMdTypeOfClaim: b.mutation<DataResponse<MasterDataTypeOfClaim>, { code: string; body: MasterDataTypeOfClaimUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/type-of-claim/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-type-of-claim-LIST' }],
    }),
    deleteMdTypeOfClaim: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/type-of-claim/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-type-of-claim-LIST' }],
    }),
  }),
})

// ---------- 23. master-data claim-condition (Access frmMdQryClaimCondition, PK organization) ----------
const masterDataClaimConditionApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdClaimConditions: b.query<PageResponse<MasterDataClaimCondition>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/claim-condition/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-claim-condition-LIST' }],
    }),
    createMdClaimCondition: b.mutation<DataResponse<MasterDataClaimCondition>, MasterDataClaimConditionCreate>({
      query: (body) => ({ url: '/master-data/claim-condition/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-claim-condition-LIST' }],
    }),
    updateMdClaimCondition: b.mutation<DataResponse<MasterDataClaimCondition>, { code: string; body: MasterDataClaimConditionUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/claim-condition/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-claim-condition-LIST' }],
    }),
    deleteMdClaimCondition: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/claim-condition/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-claim-condition-LIST' }],
    }),
  }),
})

// ---------- 24. master-data list-warehouse (Access frmMdQryListWarehouse, WH+index) ----------
const masterDataListWarehouseApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdListWarehouses: b.query<PageResponse<MasterDataListWarehouse>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/list-warehouse/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-list-warehouse-LIST' }],
    }),
    createMdListWarehouse: b.mutation<DataResponse<MasterDataListWarehouse>, MasterDataListWarehouseCreate>({
      query: (body) => ({ url: '/master-data/list-warehouse/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-list-warehouse-LIST' }],
    }),
    updateMdListWarehouse: b.mutation<DataResponse<MasterDataListWarehouse>, { code: string; body: MasterDataListWarehouseUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/list-warehouse/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-list-warehouse-LIST' }],
    }),
    deleteMdListWarehouse: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/list-warehouse/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-list-warehouse-LIST' }],
    }),
    suggestMdWarehouse: b.query<MasterDataWarehouseSuggest, void>({
      query: () => ({ url: '/master-data/list-warehouse/suggest-code' }),
      transformResponse: (r: DataResponse<MasterDataWarehouseSuggest>) => r.data ?? { index: 1, warehouse_code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdWarehouseCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({ url: '/master-data/list-warehouse/check-code', params: { warehouse_code: code } }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 25. master-data processing-type (Access frmMdQryProcessingType, POTYPE+index) ----------
const masterDataProcessingTypeApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdProcessingTypes: b.query<PageResponse<MasterDataProcessingType>, RefListQuery | void>({
      query: (params) => ({ url: '/master-data/processing-type/list', params: params ?? {} }),
      providesTags: [{ type: 'Refs', id: 'md-processing-type-LIST' }],
    }),
    createMdProcessingType: b.mutation<DataResponse<MasterDataProcessingType>, MasterDataProcessingTypeCreate>({
      query: (body) => ({ url: '/master-data/processing-type/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-processing-type-LIST' }],
    }),
    updateMdProcessingType: b.mutation<DataResponse<MasterDataProcessingType>, { code: string; body: MasterDataProcessingTypeUpdate }>({
      query: ({ code, body }) => ({ url: `/master-data/processing-type/${encodeURIComponent(code)}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'Refs', id: 'md-processing-type-LIST' }],
    }),
    deleteMdProcessingType: b.mutation<MessageResponse, string>({
      query: (code) => ({ url: `/master-data/processing-type/${encodeURIComponent(code)}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Refs', id: 'md-processing-type-LIST' }],
    }),
    suggestMdProcessingType: b.query<MasterDataProcessingTypeSuggest, void>({
      query: () => ({ url: '/master-data/processing-type/suggest-code' }),
      transformResponse: (r: DataResponse<MasterDataProcessingTypeSuggest>) => r.data ?? { index: 1, po_type_id: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdPoTypeId: b.query<{ exists: boolean }, string>({
      query: (code) => ({ url: '/master-data/processing-type/check-code', params: { po_type_id: code } }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 20. master-data dunnage (Access frmMdQryDunnage) ----------
const masterDataDunnageApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdDunnage: b.query<PageResponse<MasterDataDunnage>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/dunnage/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-dunnage-LIST' }],
    }),
    createMdDunnage: b.mutation<DataResponse<MasterDataDunnage>, MasterDataDunnageCreate>({
      query: (body) => ({
        url: '/master-data/dunnage/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-dunnage-LIST' }],
    }),
    updateMdDunnage: b.mutation<
      DataResponse<MasterDataDunnage>,
      { code: string; body: MasterDataDunnageUpdate }
    >({
      query: ({ code, body }) => ({
        url: `/master-data/dunnage/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-dunnage-LIST' }],
    }),
    deleteMdDunnage: b.mutation<MessageResponse, string>({
      query: (code) => ({
        url: `/master-data/dunnage/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-dunnage-LIST' }],
    }),
    // access-suggest: auto-gen dunnage_code (Access Suggest_IndexName).
    suggestMdDunnageCode: b.query<{ dunnage_code: string }, void>({
      query: () => ({ url: '/master-data/dunnage/suggest-code' }),
      transformResponse: (r: DataResponse<{ dunnage_code: string }>) =>
        r.data ?? { dunnage_code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdDunnageCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({
        url: '/master-data/dunnage/check-code',
        params: { dunnage_code: code },
      }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 19. master-data type-of-inspection (Access frmMdQryTypeOfInspection) ----------
const masterDataTypeOfInspectionApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdTypeOfInspection: b.query<PageResponse<MasterDataTypeOfInspection>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/type-of-inspection/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-type-of-inspection-LIST' }],
    }),
    createMdTypeOfInspection: b.mutation<DataResponse<MasterDataTypeOfInspection>, MasterDataTypeOfInspectionCreate>({
      query: (body) => ({
        url: '/master-data/type-of-inspection/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-type-of-inspection-LIST' }],
    }),
    updateMdTypeOfInspection: b.mutation<
      DataResponse<MasterDataTypeOfInspection>,
      { code: string; body: MasterDataTypeOfInspectionUpdate }
    >({
      query: ({ code, body }) => ({
        url: `/master-data/type-of-inspection/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-type-of-inspection-LIST' }],
    }),
    deleteMdTypeOfInspection: b.mutation<MessageResponse, string>({
      query: (code) => ({
        url: `/master-data/type-of-inspection/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-type-of-inspection-LIST' }],
    }),
    // access-suggest: auto-gen inspection_code (Access Suggest_IndexName).
    suggestMdInspectionCode: b.query<{ inspection_code: string }, void>({
      query: () => ({ url: '/master-data/type-of-inspection/suggest-code' }),
      transformResponse: (r: DataResponse<{ inspection_code: string }>) =>
        r.data ?? { inspection_code: '' },
      keepUnusedDataFor: 0,
    }),
    checkMdInspectionCode: b.query<{ exists: boolean }, string>({
      query: (code) => ({
        url: '/master-data/type-of-inspection/check-code',
        params: { inspection_code: code },
      }),
      transformResponse: (r: DataResponse<{ exists: boolean }>) => r.data ?? { exists: false },
      keepUnusedDataFor: 0,
    }),
  }),
})

// ---------- 18. master-data updates (Access frmMdQryUpdates, composite PK date+code) ----------
const masterDataUpdatesApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdUpdates: b.query<PageResponse<MasterDataUpdate>, RefListQuery | void>({
      query: (params) => ({
        url: '/purchase/refs/updates/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-updates-LIST' }],
    }),
    createMdUpdate: b.mutation<DataResponse<MasterDataUpdate>, MasterDataUpdateCreate>({
      query: (body) => ({
        url: '/purchase/refs/updates/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-updates-LIST' }],
    }),
    updateMdUpdate: b.mutation<
      DataResponse<MasterDataUpdate>,
      { date: string; code: string; body: MasterDataUpdateUpdate }
    >({
      query: ({ date, code, body }) => ({
        url: `/purchase/refs/updates/${encodeURIComponent(date)}/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-updates-LIST' }],
    }),
    deleteMdUpdate: b.mutation<MessageResponse, { date: string; code: string }>({
      query: ({ date, code }) => ({
        url: `/purchase/refs/updates/${encodeURIComponent(date)}/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-updates-LIST' }],
    }),
  }),
})

// ---------- 17. master-data valuation (Access frmMdQryValuation) ----------
const masterDataValuationApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdValuation: b.query<PageResponse<MasterDataValuation>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/valuation/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-valuation-LIST' }],
    }),
    createMdValuation: b.mutation<DataResponse<MasterDataValuation>, MasterDataValuationCreate>({
      query: (body) => ({
        url: '/master-data/valuation/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-valuation-LIST' }],
    }),
    updateMdValuation: b.mutation<
      DataResponse<MasterDataValuation>,
      { code: string; body: MasterDataValuationUpdate }
    >({
      query: ({ code, body }) => ({
        url: `/master-data/valuation/${encodeURIComponent(code)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-valuation-LIST' }],
    }),
    deleteMdValuation: b.mutation<MessageResponse, string>({
      query: (code) => ({
        url: `/master-data/valuation/${encodeURIComponent(code)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-valuation-LIST' }],
    }),
  }),
})

// ---------- 16. master-data general-info (Access frmMdQryGeneralInfo) ----------
const masterDataGeneralInfoApi = refsEnhanced.injectEndpoints({
  endpoints: (b) => ({
    listMdGeneralInfo: b.query<PageResponse<MasterDataGeneralInfo>, RefListQuery | void>({
      query: (params) => ({
        url: '/master-data/general-info/list',
        params: params ?? {},
      }),
      providesTags: [{ type: 'Refs', id: 'md-general-info-LIST' }],
    }),
    createMdGeneralInfo: b.mutation<DataResponse<MasterDataGeneralInfo>, MasterDataGeneralInfoCreate>({
      query: (body) => ({
        url: '/master-data/general-info/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-general-info-LIST' }],
    }),
    updateMdGeneralInfo: b.mutation<
      DataResponse<MasterDataGeneralInfo>,
      { date: string; body: MasterDataGeneralInfoUpdate }
    >({
      query: ({ date, body }) => ({
        url: `/master-data/general-info/${encodeURIComponent(date)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-general-info-LIST' }],
    }),
    deleteMdGeneralInfo: b.mutation<MessageResponse, string>({
      query: (date) => ({
        url: `/master-data/general-info/${encodeURIComponent(date)}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Refs', id: 'md-general-info-LIST' }],
    }),
  }),
})

// ---------- Re-exports ----------
export const {
  useListMdContractTypesQuery,
  useCreateMdContractTypeMutation,
  useUpdateMdContractTypeMutation,
  useDeleteMdContractTypeMutation,
} = masterDataContractTypeApi
export const {
  useListMdBanksQuery,
  useCreateMdBankMutation,
  useUpdateMdBankMutation,
  useDeleteMdBankMutation,
} = masterDataBankApi
export const {
  useListMdCertificationsQuery,
  useCreateMdCertificationMutation,
  useUpdateMdCertificationMutation,
  useDeleteMdCertificationMutation,
  useListMdCertificationPremiumChangesQuery,
  useAddMdCertificationPremiumChangeMutation,
  useDeleteMdCertificationPremiumChangeMutation,
} = masterDataCertificationApi
export const {
  useListMdDeliveryTermsQuery,
  useCreateMdDeliveryTermMutation,
  useUpdateMdDeliveryTermMutation,
  useDeleteMdDeliveryTermMutation,
} = masterDataDeliveryTermApi

export const {
  useListMdCounterPartiesQuery,
  useCreateMdCounterPartyMutation,
  useUpdateMdCounterPartyMutation,
  useDeleteMdCounterPartyMutation,
} = masterDataCounterPartyApi
export const {
  useListMdCalendarsQuery,
  useCreateMdCalendarMutation,
  useUpdateMdCalendarMutation,
  useDeleteMdCalendarMutation,
} = masterDataCalendarApi
export const {
  useListMdPackagesQuery,
  useCreateMdPackageMutation,
  useUpdateMdPackageMutation,
  useDeleteMdPackageMutation,
  useListMdPackageChangesQuery,
  useAddMdPackageChangeMutation,
  useDeleteMdPackageChangeMutation,
  useLazySuggestMdPackageCodeQuery,
  useLazyCheckMdPackageCodeQuery,
} = masterDataPackageApi
export const {
  useListMdDeliveryPlacesQuery,
  useCreateMdDeliveryPlaceMutation,
  useUpdateMdDeliveryPlaceMutation,
  useDeleteMdDeliveryPlaceMutation,
  useLazySuggestMdDeliveryPlaceCodeQuery,
  useLazyCheckMdDeliveryPlaceCodeQuery,
} = masterDataDeliveryPlaceApi
export const {
  useListMdTlcBanksQuery,
  useCreateMdTlcBankMutation,
  useUpdateMdTlcBankMutation,
  useDeleteMdTlcBankMutation,
} = masterDataTlcBankApi
export const {
  useListMdConsigneesQuery,
  useCreateMdConsigneeMutation,
  useUpdateMdConsigneeMutation,
  useDeleteMdConsigneeMutation,
  useLazySuggestMdConsigneeCodeQuery,
  useLazyCheckMdConsigneeCodeQuery,
} = masterDataConsigneeApi
export const {
  useListMdCvbbTypesQuery,
  useCreateMdCvbbTypeMutation,
  useUpdateMdCvbbTypeMutation,
  useDeleteMdCvbbTypeMutation,
} = masterDataCvbbTypeApi
export const {
  useListMdExportTerminalsQuery,
  useCreateMdExportTerminalMutation,
  useUpdateMdExportTerminalMutation,
  useDeleteMdExportTerminalMutation,
} = masterDataExportTerminalApi
export const {
  useListMdVesselNamesQuery,
  useCreateMdVesselNameMutation,
  useUpdateMdVesselNameMutation,
  useDeleteMdVesselNameMutation,
} = masterDataVesselNameApi
export const {
  useListMdLogisticCostsQuery,
  useCreateMdLogisticCostMutation,
  useUpdateMdLogisticCostMutation,
  useDeleteMdLogisticCostMutation,
} = masterDataLogisticCostApi
export const {
  useListMdShippingDocsQuery,
  useCreateMdShippingDocMutation,
  useUpdateMdShippingDocMutation,
  useDeleteMdShippingDocMutation,
  useLazySuggestMdShippingDocCodeQuery,
  useLazyCheckMdShippingDocCodeQuery,
} = masterDataShippingDocApi
export const {
  useListMdTypeOfClaimsQuery,
  useCreateMdTypeOfClaimMutation,
  useUpdateMdTypeOfClaimMutation,
  useDeleteMdTypeOfClaimMutation,
} = masterDataTypeOfClaimApi
export const {
  useListMdClaimConditionsQuery,
  useCreateMdClaimConditionMutation,
  useUpdateMdClaimConditionMutation,
  useDeleteMdClaimConditionMutation,
} = masterDataClaimConditionApi
export const {
  useListMdListWarehousesQuery,
  useCreateMdListWarehouseMutation,
  useUpdateMdListWarehouseMutation,
  useDeleteMdListWarehouseMutation,
  useLazySuggestMdWarehouseQuery,
  useLazyCheckMdWarehouseCodeQuery,
} = masterDataListWarehouseApi
export const {
  useListMdProcessingTypesQuery,
  useCreateMdProcessingTypeMutation,
  useUpdateMdProcessingTypeMutation,
  useDeleteMdProcessingTypeMutation,
  useLazySuggestMdProcessingTypeQuery,
  useLazyCheckMdPoTypeIdQuery,
} = masterDataProcessingTypeApi
export const {
  useListMdDunnageQuery,
  useCreateMdDunnageMutation,
  useUpdateMdDunnageMutation,
  useDeleteMdDunnageMutation,
  useLazySuggestMdDunnageCodeQuery,
  useLazyCheckMdDunnageCodeQuery,
} = masterDataDunnageApi
export const {
  useListMdTypeOfInspectionQuery,
  useCreateMdTypeOfInspectionMutation,
  useUpdateMdTypeOfInspectionMutation,
  useDeleteMdTypeOfInspectionMutation,
  useLazySuggestMdInspectionCodeQuery,
  useLazyCheckMdInspectionCodeQuery,
} = masterDataTypeOfInspectionApi
export const {
  useListMdUpdatesQuery,
  useCreateMdUpdateMutation,
  useUpdateMdUpdateMutation,
  useDeleteMdUpdateMutation,
} = masterDataUpdatesApi
export const {
  useListMdValuationQuery,
  useCreateMdValuationMutation,
  useUpdateMdValuationMutation,
  useDeleteMdValuationMutation,
} = masterDataValuationApi
export const {
  useListMdGeneralInfoQuery,
  useCreateMdGeneralInfoMutation,
  useUpdateMdGeneralInfoMutation,
  useDeleteMdGeneralInfoMutation,
} = masterDataGeneralInfoApi
export const {
  useListMdDocsDispatchQuery,
  useCreateMdDocDispatchMutation,
  useUpdateMdDocDispatchMutation,
  useDeleteMdDocDispatchMutation,
  useLazySuggestMdDocDispatchCodeQuery,
  useLazyCheckMdDocDispatchCodeQuery,
} = masterDataDocsDispatchApi
export const {
  useListMdServiceTypesQuery,
  useCreateMdServiceTypeMutation,
  useUpdateMdServiceTypeMutation,
  useDeleteMdServiceTypeMutation,
} = masterDataServiceTypesApi
export const {
  useListMdLicensesQuery,
  useCreateMdLicenseMutation,
  useUpdateMdLicenseMutation,
  useDeleteMdLicenseMutation,
} = masterDataLicensesApi
export const {
  useListMdServicesQuery,
  useListServiceTypesQuery,
  useSuggestServiceCodeQuery,
  useLazySuggestServiceCodeQuery,
  useCreateMdServiceMutation,
  useUpdateMdServiceMutation,
  useDeleteMdServiceMutation,
} = masterDataServicesApi
export const {
  useListCertificationsQuery: useListCertificationsCrudQuery,
  useCreateCertificationMutation,
  useUpdateCertificationMutation,
  useDeleteCertificationMutation,
} = certificationsRefApi
export const {
  useListDeliveryPlacesCrudQuery,
  useCreateDeliveryPlaceMutation,
  useUpdateDeliveryPlaceMutation,
  useDeleteDeliveryPlaceMutation,
} = deliveryPlacesRefApi
export const {
  useListDeliveryTermsCrudQuery,
  useCreateDeliveryTermMutation,
  useUpdateDeliveryTermMutation,
  useDeleteDeliveryTermMutation,
} = deliveryTermsRefApi
export const {
  useListPackagesCrudQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
} = packagesRefApi
export const {
  useListAdjustLotsQuery,
  useCreateAdjustLotMutation,
  useUpdateAdjustLotMutation,
  useDeleteAdjustLotMutation,
} = adjustLotsRefApi
export const {
  useListAdminLicensesQuery,
  useCreateAdminLicenseMutation,
  useUpdateAdminLicenseMutation,
  useDeleteAdminLicenseMutation,
} = licensesAdminRefApi
export const {
  useListQualitiesCrudQuery,
  useCreateQualityMutation,
  useUpdateQualityMutation,
  useDeleteQualityMutation,
} = qualitiesRefApi
export const {
  useListUpdatesQuery,
  useCreateUpdateMutation,
  useUpdateUpdateMutation,
  useDeleteUpdateMutation,
} = updatesRefApi
export const {
  useListCalendarsQuery,
  useCreateCalendarMutation,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
} = calendarsRefApi
export const {
  useListMonthsQuery,
  useCreateMonthMutation,
  useUpdateMonthMutation,
  useDeleteMonthMutation,
} = monthsRefApi
export const {
  useListHolidaysQuery,
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
} = holidaysRefApi
export const {
  useListMdCounterPartyBanksQuery,
  useCreateMdCounterPartyBankMutation,
  useUpdateMdCounterPartyBankMutation,
  useDeleteMdCounterPartyBankMutation,
} = masterDataCounterPartyBankApi
